import React, { useState } from 'react';
import { storeGetOrDefault, storeSet, DEF_ABOUT } from '../../store';
import '../admin.css';

const Toast = ({ msg, type }) => msg ? <div className={`adm-toast adm-toast-${type}`}>{msg}</div> : null;

const AboutAdmin = () => {
  const [data, setData] = useState(() => storeGetOrDefault('about', DEF_ABOUT));
  const [toast, setToast] = useState({ msg: '', type: 'success' });

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast({ msg: '', type: 'success' }), 3000);
  };

  const handleSave = (e) => {
    e.preventDefault();
    storeSet('about', data);
    showToast('About Us page saved successfully!');
  };

  const handleReset = () => {
    if (window.confirm('Reset About Us page to default content?')) {
      storeSet('about', DEF_ABOUT);
      setData(DEF_ABOUT);
      showToast('Reset to defaults.');
    }
  };

  // Generic updater helpers
  const setHero      = (k) => (e) => setData(d => ({ ...d, hero:    { ...d.hero,    [k]: e.target.value } }));
  const setIntro     = (k) => (e) => setData(d => ({ ...d, intro:   { ...d.intro,   [k]: e.target.value } }));
  const setMission   = (k) => (e) => setData(d => ({ ...d, mission: { ...d.mission, [k]: e.target.value } }));
  const setVision    = (k) => (e) => setData(d => ({ ...d, vision:  { ...d.vision,  [k]: e.target.value } }));
  const setExpertise = (k) => (e) => setData(d => ({ ...d, expertise: { ...d.expertise, [k]: e.target.value } }));
  const setJgzt      = (k) => (e) => setData(d => ({ ...d, jgzt:    { ...d.jgzt,    [k]: e.target.value } }));

  const setStatField = (idx, k) => (e) =>
    setData(d => ({ ...d, hero: { ...d.hero, stats: d.hero.stats.map((s, i) => i === idx ? { ...s, [k]: e.target.value } : s) } }));

  const setHighlight = (idx) => (e) =>
    setData(d => ({ ...d, intro: { ...d.intro, highlights: d.intro.highlights.map((h, i) => i === idx ? e.target.value : h) } }));

  const setExpertiseItem = (idx, k) => (e) =>
    setData(d => ({ ...d, expertise: { ...d.expertise, items: d.expertise.items.map((it, i) => i === idx ? { ...it, [k]: e.target.value } : it) } }));

  const setJgztCard = (idx, k) => (e) =>
    setData(d => ({ ...d, jgzt: { ...d.jgzt, cards: d.jgzt.cards.map((c, i) => i === idx ? { ...c, [k]: e.target.value } : c) } }));

  return (
    <div className='adm-page'>
      <Toast {...toast} />

      <div className='adm-page-hero'>
        <div className='adm-page-hero-icon' style={{ background: 'linear-gradient(135deg,#1a5fa3,#2878c4)' }}>
          <svg width='22' height='22' viewBox='0 0 24 24' fill='none' stroke='#fff' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
            <path d='M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2'/><circle cx='9' cy='7' r='4'/>
            <path d='M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75'/>
          </svg>
        </div>
        <div>
          <h1 className='adm-title'>About Us</h1>
          <p className='adm-subtitle'>Control every section of the About Us page — hero, company intro, mission/vision, expertise, and JGZT cards</p>
        </div>
      </div>

      <form onSubmit={handleSave}>
        <div className='adm-settings-grid'>

          {/* ── Hero Section ── */}
          <div className='adm-settings-card'>
            <h3 className='adm-settings-card-title'>🏷 Hero Section</h3>
            <div className='adm-form' style={{ gap: 14 }}>
              <div className='adm-form-row'>
                <div className='adm-form-group'>
                  <label className='adm-label'>Badge Text</label>
                  <input className='adm-input' value={data.hero.badge} onChange={setHero('badge')} placeholder='About Us' />
                </div>
              </div>
              <div className='adm-form-group'>
                <label className='adm-label'>Hero Title</label>
                <input className='adm-input' value={data.hero.title} onChange={setHero('title')} placeholder='John Galt International' />
              </div>
              <div className='adm-form-group'>
                <label className='adm-label'>Hero Subtitle</label>
                <textarea className='adm-input adm-textarea' rows={2} value={data.hero.subtitle} onChange={setHero('subtitle')} placeholder='Leaders in heavy engineering...' />
              </div>

              <div>
                <label className='adm-label' style={{ marginBottom: 10, display: 'block' }}>Stats (4 boxes shown in hero)</label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  {data.hero.stats.map((stat, i) => (
                    <div key={stat.id} style={{ background: '#f8faff', border: '1px solid #e5e9f4', borderRadius: 10, padding: '12px 14px' }}>
                      <p style={{ fontSize: '.75rem', fontWeight: 700, color: '#1a5fa3', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '.5px' }}>Stat {i + 1}</p>
                      <div className='adm-form-group' style={{ marginBottom: 8 }}>
                        <label className='adm-label'>Value</label>
                        <input className='adm-input' value={stat.value} onChange={setStatField(i, 'value')} placeholder='1978' />
                      </div>
                      <div className='adm-form-group'>
                        <label className='adm-label'>Label</label>
                        <input className='adm-input' value={stat.label} onChange={setStatField(i, 'label')} placeholder='Founded' />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* ── Company Intro ── */}
          <div className='adm-settings-card'>
            <h3 className='adm-settings-card-title'>🏢 Company Introduction</h3>
            <div className='adm-form' style={{ gap: 14 }}>
              <div className='adm-form-row'>
                <div className='adm-form-group'>
                  <label className='adm-label'>Section Tag</label>
                  <input className='adm-input' value={data.intro.tag} onChange={setIntro('tag')} placeholder='Our Company' />
                </div>
              </div>
              <div className='adm-form-group'>
                <label className='adm-label'>Section Heading</label>
                <input className='adm-input' value={data.intro.heading} onChange={setIntro('heading')} placeholder='About John Galt International' />
              </div>
              <div className='adm-form-group'>
                <label className='adm-label'>Paragraph</label>
                <textarea className='adm-input adm-textarea' rows={4} value={data.intro.paragraph} onChange={setIntro('paragraph')} placeholder='John Galt International is a...' />
              </div>

              <div>
                <label className='adm-label' style={{ marginBottom: 10, display: 'block' }}>Bullet Highlights (4 items)</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {data.intro.highlights.map((hl, i) => (
                    <div key={i} className='adm-form-group' style={{ marginBottom: 0 }}>
                      <label className='adm-label'>Highlight {i + 1}</label>
                      <input className='adm-input' value={hl} onChange={setHighlight(i)} placeholder={`Highlight point ${i + 1}`} />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* ── Mission & Vision ── */}
          <div className='adm-settings-card'>
            <h3 className='adm-settings-card-title'>🎯 Mission &amp; Vision</h3>
            <div className='adm-form' style={{ gap: 14 }}>
              <div style={{ background: '#f0f7ff', border: '1px solid #bfdbfe', borderRadius: 10, padding: '14px 16px' }}>
                <p style={{ fontSize: '.8rem', fontWeight: 700, color: '#1a5fa3', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '.5px' }}>Mission</p>
                <div className='adm-form-group' style={{ marginBottom: 8 }}>
                  <label className='adm-label'>Heading</label>
                  <input className='adm-input' value={data.mission.heading} onChange={setMission('heading')} placeholder='Our Mission' />
                </div>
                <div className='adm-form-group'>
                  <label className='adm-label'>Text</label>
                  <textarea className='adm-input adm-textarea' rows={3} value={data.mission.text} onChange={setMission('text')} placeholder='Mission statement...' />
                </div>
              </div>
              <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10, padding: '14px 16px' }}>
                <p style={{ fontSize: '.8rem', fontWeight: 700, color: '#16a34a', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '.5px' }}>Vision</p>
                <div className='adm-form-group' style={{ marginBottom: 8 }}>
                  <label className='adm-label'>Heading</label>
                  <input className='adm-input' value={data.vision.heading} onChange={setVision('heading')} placeholder='Our Vision' />
                </div>
                <div className='adm-form-group'>
                  <label className='adm-label'>Text</label>
                  <textarea className='adm-input adm-textarea' rows={3} value={data.vision.text} onChange={setVision('text')} placeholder='Vision statement...' />
                </div>
              </div>
            </div>
          </div>

          {/* ── Core Expertise ── */}
          <div className='adm-settings-card'>
            <h3 className='adm-settings-card-title'>⚙️ Core Expertise</h3>
            <div className='adm-form' style={{ gap: 14 }}>
              <div className='adm-form-row'>
                <div className='adm-form-group'>
                  <label className='adm-label'>Section Tag</label>
                  <input className='adm-input' value={data.expertise.tag} onChange={setExpertise('tag')} placeholder='What We Build' />
                </div>
              </div>
              <div className='adm-form-group'>
                <label className='adm-label'>Section Heading</label>
                <input className='adm-input' value={data.expertise.heading} onChange={setExpertise('heading')} placeholder='Our Core Expertise' />
              </div>

              <div>
                <label className='adm-label' style={{ marginBottom: 10, display: 'block' }}>Expertise Cards (6 items)</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {data.expertise.items.map((item, i) => (
                    <div key={item.id} style={{ background: '#f8faff', border: '1px solid #e5e9f4', borderRadius: 10, padding: '12px 14px' }}>
                      <p style={{ fontSize: '.75rem', fontWeight: 700, color: '#1a5fa3', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '.5px' }}>Item {i + 1}</p>
                      <div className='adm-form-group' style={{ marginBottom: 8 }}>
                        <label className='adm-label'>Title</label>
                        <input className='adm-input' value={item.title} onChange={setExpertiseItem(i, 'title')} placeholder='Expertise title' />
                      </div>
                      <div className='adm-form-group'>
                        <label className='adm-label'>Description</label>
                        <textarea className='adm-input adm-textarea' rows={2} value={item.desc} onChange={setExpertiseItem(i, 'desc')} placeholder='Brief description...' />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* ── JGZT Section ── */}
          <div className='adm-settings-card'>
            <h3 className='adm-settings-card-title'>🏭 John Galt Zinga Technologies (JGZT)</h3>
            <div className='adm-form' style={{ gap: 14 }}>
              <div className='adm-form-row'>
                <div className='adm-form-group'>
                  <label className='adm-label'>Section Tag</label>
                  <input className='adm-input' value={data.jgzt.tag} onChange={setJgzt('tag')} placeholder='Part of the Group' />
                </div>
              </div>
              <div className='adm-form-group'>
                <label className='adm-label'>Section Heading</label>
                <input className='adm-input' value={data.jgzt.heading} onChange={setJgzt('heading')} placeholder='John Galt Zinga Technologies' />
              </div>

              <div>
                <label className='adm-label' style={{ marginBottom: 10, display: 'block' }}>Cards (3 cards)</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {data.jgzt.cards.map((card, i) => (
                    <div key={card.id} style={{ background: '#f8faff', border: '1px solid #e5e9f4', borderRadius: 10, padding: '12px 14px' }}>
                      <p style={{ fontSize: '.75rem', fontWeight: 700, color: '#1a5fa3', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '.5px' }}>Card {i + 1}</p>
                      <div className='adm-form-row'>
                        <div className='adm-form-group'>
                          <label className='adm-label'>Tag / Badge</label>
                          <input className='adm-input' value={card.tag} onChange={setJgztCard(i, 'tag')} placeholder='Principal Importer' />
                        </div>
                        <div className='adm-form-group'>
                          <label className='adm-label'>Card Heading</label>
                          <input className='adm-input' value={card.heading} onChange={setJgztCard(i, 'heading')} placeholder='Card heading...' />
                        </div>
                      </div>
                      <div className='adm-form-group'>
                        <label className='adm-label'>Card Text</label>
                        <textarea className='adm-input adm-textarea' rows={3} value={card.text} onChange={setJgztCard(i, 'text')} placeholder='Card description...' />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

        </div>

        {/* Action Bar */}
        <div style={{ display: 'flex', gap: 12, marginTop: 24 }}>
          <button type='submit' className='adm-btn adm-btn-primary' style={{ flex: 1 }}>
            💾 Save About Us Page
          </button>
          <button type='button' className='adm-btn adm-btn-danger' onClick={handleReset}>
            ↩ Reset Defaults
          </button>
        </div>
      </form>
    </div>
  );
};

export default AboutAdmin;
